import { FlagReason } from '@modules/HappinessModule/enums/FlagsReasons';
import { FlagStatus } from '@modules/HappinessModule/enums/FlagStatus';
import { FlagFields } from '@modules/HappinessModule/interfaces/FlagFields';
import * as Yup from 'yup';
import { FlagFormFields } from './FlagFormFields';
import { flagReasonsOptions } from './flagsReasonsObject';
import { NewHomeAvailableFromTypes } from '@modules/HappinessModule/enums/NewHomeAvailableFromTypes';
import { NewProjectAssignmentData } from '@modules/HappinessModule/interfaces/NewProjectAssignmentData';
import { EmploymentType } from '@shared/enums/EmploymentType';
import { currencies } from '@shared/enums/currencies';
import { AmountType } from '@modules/HappinessModule/enums/AmountType';

type validationSchemaType = {
  [key in keyof Partial<FlagFields>]: Yup.AnySchema;
};

const errorMessages = {
  [FlagFormFields.severity.name]: 'Please select severity',
  [FlagFormFields.description.name]: 'Please enter description',
  [FlagFormFields.primaryTrigger.name]: 'Please select primary reason',
  [FlagFormFields.primarySubReason.name]: 'Please select primary sub-reason',
  [FlagFormFields.secondarySubReason.name]: 'Please select secondary sub-reason',
  [FlagFormFields.isNewHomeNeeded.name]: 'Please select if NewHome is needed',
  [FlagFormFields.resolutionNotes.name]: 'Please enter a resolution notes',
  [FlagFormFields.whatWasDone.name]: 'Please enter what was done',
  [FlagFormFields.suggestedActionItems.name]: 'Please enter suggested action items',
  [FlagFormFields.pros.name]: 'Please enter pros',
  [FlagFormFields.cons.name]: 'Please enter cons',
  [FlagFormFields.softSkills.name]: 'Please enter soft skills',
  [FlagFormFields.offerRejectionReason.name]: 'Please enter rejection reason',
  [FlagFormFields.companyName.name]: 'Please enter company name',
  [FlagFormFields.role.name]: 'Please enter title/role',
  [FlagFormFields.hiringType.name]: 'Please select hiring type',
  [FlagFormFields.compensation.name]: 'Please enter compensation',
  [FlagFormFields.currencyType.name]: 'Please select currency type',
  [FlagFormFields.amountType.name]: 'Please select compensation type',
  [FlagFormFields.benefits.name]: 'Please enter benefits and bonuses',
  [FlagFormFields.newHomeResolution.name]:
    "Unable to close flag: The 'NewHome' resolution must be set first.\n Please contact the Talent Operations Team for assistance.",
  [FlagFormFields.offerAcceptanceStatus.name]: 'Please select offer acceptance status',
  [`${FlagFormFields.offerAcceptanceStatus.name}Close`]:
    "Unable to close flag: 'Offer Acceptance Status' must be set to Accepted or Rejected first.",
  [FlagFormFields.newHomeAvailableFromType.name]: 'Please select exact date',
  [FlagFormFields.departments.name]: 'Please select at least one affected project',
};

const checkIfThereAreSubReasons = (reason: FlagReason | null) =>
  [...flagReasonsOptions.involuntary.options, ...flagReasonsOptions.voluntary.options].some(
    ({ value, subReasons }) => value === reason && subReasons && subReasons?.length > 0
  );

type validationSchemaNewProjectAssignmentType = {
  [key in keyof Partial<NewProjectAssignmentData>]: Yup.AnySchema;
};

const validationSchemaNewProjectAssignment: validationSchemaNewProjectAssignmentType = {
  startDate: Yup.date().typeError('Set a valid date').nullable(),
};

export const getValidationSchema = (isGeneralFlagInformationEditable: boolean) => {
  const fieldsSchema: validationSchemaType = {
    newProjectAssignment: Yup.object(validationSchemaNewProjectAssignment).nullable(),
    severity: Yup.string().required(errorMessages.severity).nullable(),
    description: Yup.string().required(errorMessages.description).nullable(),
    isNewHomeNeeded: Yup.string().nullable(),
    primaryTrigger: Yup.string().required(errorMessages.primaryTrigger).nullable(),
    primarySubReason: Yup.string()
      .when(FlagFormFields.primaryTrigger.name, {
        is: (primaryTrigger: FlagReason | null) => checkIfThereAreSubReasons(primaryTrigger),
        then: Yup.string().required(errorMessages.primarySubReason).nullable(),
      })
      .nullable(),
    secondarySubReason: Yup.string()
      .when(FlagFormFields.secondaryTrigger.name, {
        is: (secondaryTrigger: FlagReason | null) => checkIfThereAreSubReasons(secondaryTrigger),
        then: Yup.string().required(errorMessages.secondarySubReason).nullable(),
      })
      .nullable(),
    resolutionNotes: Yup.string()
      .when(FlagFormFields.status.name, {
        is: (status: FlagStatus) => status === FlagStatus.Positive || status === FlagStatus.Negative,
        then: Yup.string().required(errorMessages.resolutionNotes).nullable(),
      })
      .nullable(),
    lastDayOnTheProject: Yup.date().typeError('Set a valid date').nullable(),
    whatWasDone: Yup.string().required(errorMessages.whatWasDone).nullable(),
    suggestedActionItems: Yup.string().required(errorMessages.suggestedActionItems).nullable(),
    pros: Yup.string()
      .when(FlagFormFields.isNewHomeNeeded.name, {
        is: (isNewHomeNeeded: string) => isNewHomeNeeded === 'true',
        then: Yup.string().required(errorMessages.pros).nullable(),
      })
      .nullable(),
    cons: Yup.string()
      .when(FlagFormFields.isNewHomeNeeded.name, {
        is: (isNewHomeNeeded: string) => isNewHomeNeeded === 'true',
        then: Yup.string().required(errorMessages.cons).nullable(),
      })
      .nullable(),
    softSkills: Yup.string()
      .when(FlagFormFields.isNewHomeNeeded.name, {
        is: (isNewHomeNeeded: string) => isNewHomeNeeded === 'true',
        then: Yup.string().required(errorMessages.softSkills).nullable(),
      })
      .nullable(),
    newHomeResolution: Yup.string()
      .test(
        'сheck if the resolution of the new home process is settled before the flag is closed when new home is selected',
        errorMessages.newHomeResolution,
        (value, context) => {
          const isNewHomeNeeded = context.parent[FlagFormFields.isNewHomeNeeded.name];
          const flagStatus = context.parent[FlagFormFields.status.name];

          if (
            isNewHomeNeeded === 'true' &&
            (flagStatus === FlagStatus.Negative || flagStatus === FlagStatus.Positive)
          ) {
            return !value ? false : true;
          }

          return true;
        }
      )
      .nullable(),
    newHomeAvailableFrom: Yup.string()
      .when(FlagFormFields.newHomeAvailableFromType.name, {
        is: (newHomeAvailableFromType: NewHomeAvailableFromTypes) =>
          newHomeAvailableFromType === NewHomeAvailableFromTypes.ExactDate,
        then: Yup.string().required(errorMessages.newHomeAvailableFromType).nullable(),
      })
      .nullable(),
    companyName: Yup.string()
      .when(
        [
          FlagFormFields.isAnotherOfferInvolved.name,
          FlagFormFields.status.name,
          FlagFormFields.companyNameNotProvided.name,
        ],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, companyNameNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !companyNameNotProvided,
          then: Yup.string().required(errorMessages.companyName).nullable(),
        }
      )
      .nullable(),
    role: Yup.string()
      .when(
        [FlagFormFields.isAnotherOfferInvolved.name, FlagFormFields.status.name, FlagFormFields.roleNotProvided.name],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, roleNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !roleNotProvided,
          then: Yup.string().required(errorMessages.role).nullable(),
        }
      )
      .nullable(),
    hiringType: Yup.string()
      .oneOf([...Object.values(EmploymentType), null, ''], errorMessages.hiringType)
      .when(
        [
          FlagFormFields.isAnotherOfferInvolved.name,
          FlagFormFields.status.name,
          FlagFormFields.hiringTypeNotProvided.name,
        ],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, hiringTypeNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !hiringTypeNotProvided,
          then: Yup.string().required(errorMessages.hiringType).nullable(),
        }
      )
      .nullable(),
    compensation: Yup.number()
      .when(
        [
          FlagFormFields.isAnotherOfferInvolved.name,
          FlagFormFields.status.name,
          FlagFormFields.compensationNotProvided.name,
        ],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, compensationNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !compensationNotProvided,
          then: Yup.number().required(errorMessages.compensation).nullable(),
        }
      )
      .nullable(),
    currencyType: Yup.string()
      .oneOf([...Object.values(currencies), null, ''], errorMessages.currencyType)
      .when(
        [
          FlagFormFields.isAnotherOfferInvolved.name,
          FlagFormFields.status.name,
          FlagFormFields.currencyTypeNotProvided.name,
        ],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, currencyTypeNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !currencyTypeNotProvided,
          then: Yup.string().required(errorMessages.currencyType).nullable(),
        }
      )
      .nullable(),
    amountType: Yup.string()
      .oneOf([...Object.values(AmountType), null, ''], errorMessages.amountType)
      .when(
        [
          FlagFormFields.isAnotherOfferInvolved.name,
          FlagFormFields.status.name,
          FlagFormFields.amountTypeNotProvided.name,
        ],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, amountTypeNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !amountTypeNotProvided,
          then: Yup.string().required(errorMessages.amountType).nullable(),
        }
      )
      .nullable(),
    benefits: Yup.string()
      .when(
        [
          FlagFormFields.isAnotherOfferInvolved.name,
          FlagFormFields.status.name,
          FlagFormFields.benefitsNotProvided.name,
        ],
        {
          is: (isAnotherOfferInvolved: boolean, status: FlagStatus, benefitsNotProvided: boolean) =>
            isAnotherOfferInvolved &&
            (status === FlagStatus.Positive || status === FlagStatus.Negative) &&
            !benefitsNotProvided,
          then: Yup.string().required(errorMessages.benefits).nullable(),
        }
      )
      .nullable(),
    offerRejectionReason: Yup.string()
      .when([FlagFormFields.isAnotherOfferInvolved.name, FlagFormFields.offerAcceptanceStatus.name], {
        is: (isAnotherOfferInvolved: boolean, offerAcceptanceStatus: string) =>
          isAnotherOfferInvolved && offerAcceptanceStatus === 'rejected',
        then: Yup.string().required(errorMessages.offerRejectionReason).nullable(),
      })
      .nullable(),
    offerAcceptanceStatus: Yup.string()
      .when(FlagFormFields.isAnotherOfferInvolved.name, {
        is: true,
        then: Yup.string().required(errorMessages.offerAcceptanceStatus).nullable(),
      })
      .test(
        'check-offer-acceptance-status-when-closing',
        errorMessages[`${FlagFormFields.offerAcceptanceStatus.name}Close`],
        (value, context) => {
          const isAnotherOfferInvolved = context.parent[FlagFormFields.isAnotherOfferInvolved.name];
          const flagStatus = context.parent[FlagFormFields.status.name];

          if (isAnotherOfferInvolved && (flagStatus === FlagStatus.Positive || flagStatus === FlagStatus.Negative)) {
            return value === 'accepted' || value === 'rejected';
          }

          return true;
        }
      )
      .nullable(),
    departments: Yup.array().min(isGeneralFlagInformationEditable ? 1 : 0, errorMessages.departments),
  };

  return Yup.object().shape(fieldsSchema);
};
